import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import CommonTable from "../../../components/Common/TableContainer";
import {
  fetchAIScheduleMatrixGroupTableAndFilterSchema,
  getAIScheduleMatrixGroupList,
} from "model/AIScheduleMatrixGroupModel";
import AIScheduleMatrixGroupAddEditModal from "pages/AllCommonModal/AIScheduleMatrixGroupAddEditModal/ai-schedule-matrix-group-add-edit-modal";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";

const METADATA_CACHE_KEY = "metadata:aiScheduleMatrixGroup";

const extractList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const mapRows = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    ...item,
    id: item?.id ?? item?.Id ?? item?._id,
    ID: item?.id ?? item?.Id ?? item?._id,
  }));

const AIScheduleMatrixGroup = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();

  const bootstrapStartedRef = useRef(false);

  const [pageMetadata, setPageMetadata] = useState(null);
  const [aiScheduleMatrixGroupData, setAIScheduleMatrixGroupData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);

  const tableMetaData = useMemo(
    () =>
      pageMetadata?.table?.columns?.map((col) => ({
        Header: col?.title,
        accessor: col?.field,
        type: col?.type,
      })),
    [pageMetadata]
  );

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return aiScheduleMatrixGroupData;

    const query = searchQuery.trim().toLowerCase();
    return aiScheduleMatrixGroupData.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(query)
      )
    );
  }, [aiScheduleMatrixGroupData, searchQuery]);

  const actionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.AIScheduleMatrixGroupAddAccess),
      canEdit: Boolean(permissionData?.AIScheduleMatrixGroupUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyRowAction = actionPermissions.canEdit;

  const loadPageData = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const [metaResponse, listResponse] = await Promise.all([
          fetchMetadataSchema(METADATA_CACHE_KEY, () =>
            fetchAIScheduleMatrixGroupTableAndFilterSchema([])
          ),
          getAIScheduleMatrixGroupList(params),
        ]);

        if (metaResponse?.success) {
          setPageMetadata(metaResponse.data);
        }

        const list = extractList(listResponse);
        setAIScheduleMatrixGroupData(listResponse?.success ? mapRows(list) : []);
      } finally {
        setLoading(false);
      }
    },
    [fetchMetadataSchema]
  );

  const refreshList = useCallback(async (params = {}) => {
    const listResponse = await getAIScheduleMatrixGroupList(params);
    const list = extractList(listResponse);
    setAIScheduleMatrixGroupData(listResponse?.success ? mapRows(list) : []);
  }, []);

  useEffect(() => {
    if (bootstrapStartedRef.current) return;
    bootstrapStartedRef.current = true;
    loadPageData();
  }, [loadPageData]);

  const handleEdit = useCallback((row) => {
    setEditItemId(row?.id ?? null);
    setEditModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditItemId(null);
    setEditModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
    setEditItemId(null);
  }, []);

  const handleSearchSubmit = useCallback(
    async (value) => {
      await refreshList({ search: value });
    },
    [refreshList]
  );

  const isTableReady = !loading && Boolean(tableMetaData?.length);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">AI Schedule Matrix Group</h5>

                {actionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button color="primary" onClick={handleCreate}>
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by title"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value || "")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery.trim());
                        }
                      }}
                    />
                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {isTableReady ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyRowAction}
                  onEdit={actionPermissions.canEdit ? handleEdit : undefined}
                  isEditAccess={actionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={false}
                  pageName="AI Schedule Matrix Group"
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AIScheduleMatrixGroupAddEditModal
            isOpen={editModalOpen}
            onClose={handleCloseModal}
            itemId={editItemId}
            onSuccess={refreshList}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

AIScheduleMatrixGroup.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AIScheduleMatrixGroup);
